<template>
    <div class="grid-wrapper margin--3xl">
        <div class="offering-listing__header margin--l">
            <h2>
                {{ config.i18n.upcoming }}
            </h2>
            <p
                v-if="config.allTotal"
                class="offering-listing__count body--small color--accent-color-dark-grey"
                aria-live="polite"
            >
                {{ countLabel }}
            </p>
        </div>

        <EventFilters
            :month="filters.month"
            :category="filters.category"
            :type="filters.type"
            :months="config.months"
            :categories="config.categories"
            :has-filters="hasFilters"
            :page-url="config.pageUrl"
            :clear-month-url="buildUrl({ month: '' })"
            :clear-category-url="buildUrl({ category: '', type: '' })"
            :clear-type-url="buildUrl({ type: '' })"
            :icons="config.icons"
            :i18n="config.i18n"
            @update:month="applyFilter('month', $event)"
            @update:category="applyFilter('category', $event)"
            @update:type="applyFilter('type', $event)"
            @reset="reset"
        />

        <EventList
            v-if="items.length"
            ref="list"
            :items="items"
            :icons="config.icons"
        >
            <ResourceLoadMore
                v-if="hasMore || loading"
                :loading="loading"
                :next-url="nextUrl"
                :label="config.i18n.loadMore"
                @load="loadMore"
            />
        </EventList>

        <div v-else class="search-results__empty" role="status">
            <span v-html="config.icons.info"></span>
            <h5 class="body--large">
                {{ config.i18n.noResults }}
            </h5>
        </div>
    </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref } from 'vue'

import ResourceLoadMore from '../resources/ResourceLoadMore.vue'
import EventFilters from './EventFilters.vue'
import EventList from './EventList.vue'

const props = defineProps({
    config: { type: Object, required: true },
})

const config = props.config

const filters = reactive({
    month: config.filters.month || '',
    category: config.filters.category || '',
    type: config.filters.type || '',
})

const items = ref(config.items || [])
const total = ref(config.total || 0)
const maxPages = ref(config.maxPages || 0)
const page = ref(config.page || 1)
const loading = ref(false)
const list = ref(null)

let request = null

const hasFilters = computed(
    () => filters.month !== '' || filters.category !== '' || filters.type !== ''
)

const hasMore = computed(() => page.value < maxPages.value)

const countLabel = computed(() =>
    (config.i18n.showing || '')
        .replace('%1$s', total.value.toLocaleString())
        .replace('%2$s', (config.allTotal || 0).toLocaleString())
)

const queryString = (overrides = {}) => {
    const state = { ...filters, ...overrides }
    const params = new URLSearchParams()

    if (state.month) {
        params.set('event_month', state.month)
    }

    if (state.category) {
        params.set('event_category', state.category)
    }

    if (state.type) {
        params.set('event_type', state.type)
    }

    const query = params.toString()

    return query ? `?${query}` : ''
}

const buildUrl = (overrides = {}) => config.pageUrl + queryString(overrides)

const nextUrl = computed(() => {
    const base = config.pageUrl.replace(/\/?$/, '/')

    return `${base}page/${page.value + 1}/${queryString()}`
})

const fetchPage = async (requested, { append }) => {
    if (request) {
        request.abort()
    }

    request = new AbortController()
    loading.value = true

    const params = new URLSearchParams({
        month: filters.month,
        category: filters.category,
        type: filters.type,
        page: String(requested),
        per_page: String(config.perPage),
    })

    if (config.lang) {
        params.set('lang', config.lang)
    }

    try {
        const response = await fetch(`${config.restUrl}?${params.toString()}`, {
            signal: request.signal,
            headers: { Accept: 'application/json' },
        })

        if (!response.ok) {
            throw new Error(`Request failed with ${response.status}`)
        }

        const data = await response.json()
        const previous = items.value.length

        items.value = append ? items.value.concat(data.items) : data.items
        total.value = data.total
        maxPages.value = data.max_pages
        page.value = data.page

        if (append) {
            await nextTick()
            list.value?.focusItemAt(previous)
        }

        loading.value = false
        request = null
    } catch (error) {
        if (error.name !== 'AbortError') {
            loading.value = false
            request = null
        }
    }
}

const applyFilter = (key, value) => {
    if (filters[key] === value) {
        return
    }

    filters[key] = value

    if (key === 'category') {
        filters.type = ''
    }

    window.history.pushState({ ...filters }, '', buildUrl())

    fetchPage(1, { append: false })
}

const reset = () => {
    if (!hasFilters.value) {
        return
    }

    filters.month = ''
    filters.category = ''
    filters.type = ''

    window.history.pushState({ ...filters }, '', config.pageUrl)

    fetchPage(1, { append: false })
}

const loadMore = () => {
    if (loading.value || !hasMore.value) {
        return
    }

    fetchPage(page.value + 1, { append: true })
}

const onPopState = () => {
    const params = new URLSearchParams(window.location.search)

    filters.month = params.get('event_month') || ''
    filters.category = params.get('event_category') || ''
    filters.type = params.get('event_type') || ''

    fetchPage(1, { append: false })
}

onMounted(() => {
    window.addEventListener('popstate', onPopState)
})

onBeforeUnmount(() => {
    window.removeEventListener('popstate', onPopState)
    request?.abort()
})
</script>
